import { useQuery } from '@apollo/client/react';
import type { ReactNode } from 'react';
import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import Loader from '../../components/common/Loader';
import i18n from '../../i18n';
import { FetchCompanyDetails } from '../../pages/CompanyManagementPage/shared/Companies.graphql';
import extractFromJWT from '../../utils/extractFromJWT';
import { getToken, onTokenUpdate } from '../../utils/session';
import { FetchCurrentUser } from './Usersession.graphql';
import useSessionLifecycle from './useSessionLifecycle';
import type { CompanyItemDataFragment, CurrentUserFragment, RoleAssignmentFragment } from '@/gql/graphql';

export interface ContextValue {
    token: string | null;
    user: CurrentUserFragment | null;
    activeRole: RoleAssignmentFragment | null;
    sessionId: string | null;
    selectedCompanyId: string | null;
    company: CompanyItemDataFragment | null;
    logout: () => void;
}

export const Context = createContext<ContextValue | null>(null);

export const useUserSession = () => {
    const context = useContext(Context);

    if (!context) {
        throw new Error('useUserSession must be used within a UserSessionProvider');
    }

    return context;
};

export function useUser(shouldThrowIfNull: true): CurrentUserFragment;
export function useUser(shouldThrowIfNull: false | undefined): CurrentUserFragment | null;
export function useUser(shouldThrowIfNull?: boolean): CurrentUserFragment | null;
export function useUser(shouldThrowIfNull = false): CurrentUserFragment | null {
    const { user } = useUserSession(); // Assuming useUserSession is correctly defined and imported

    if (shouldThrowIfNull && !user) {
        throw new Error('User is not available');
    }

    return user;
}

// this function returns the active role assignment of the user. Superadmin
export function useActiveRole(): RoleAssignmentFragment | null {
    const { activeRole } = useUserSession();

    return activeRole;
}

export interface CompanyContextValue {
    /** Active company id (tenant user's company, or super admin's currently-selected) — null when super admin has not picked one. */
    companyId: string | null;
    /** Super-admin-specific: the company id currently selected via CompanySelect. Null for tenant users. */
    selectedCompanyId: string | null;
    /** Full company document for the active company. Null when companyId is null or doc is still loading. */
    company: CompanyItemDataFragment | null;
    /** True when the active company is a demo tenant. False when no company is active. */
    isDemo: boolean;
    /** True when the logged-in user is a super admin (regardless of whether they have switched into a company). */
    isSuperAdmin: boolean;
}

export interface RequiredCompanyContextValue extends CompanyContextValue {
    companyId: string;
    company: CompanyItemDataFragment;
}

/**
 * Unified hook for accessing the active company context.
 * Pass `true` inside a render-guarded subtree to narrow `companyId` and `company` to non-null —
 * removes the need for `|| ''` fallbacks when the caller already knows a company is active.
 */
export function useCompany(throwIfMissing: true): RequiredCompanyContextValue;
export function useCompany(throwIfMissing?: false): CompanyContextValue;
export function useCompany(throwIfMissing = false): CompanyContextValue {
    const { user, selectedCompanyId, company } = useUserSession();

    const companyId = user?.__typename === 'TenantUser' && user.company ? user.company.id : selectedCompanyId;

    if (throwIfMissing && (!companyId || !company)) {
        throw new Error('Active company is required but none is set');
    }

    return {
        companyId,
        selectedCompanyId,
        company,
        isDemo: company?.isDemo ?? false,
        isSuperAdmin: user?.__typename === 'SuperAdminUser',
    };
}

export interface UserSessionProviderProps {
    children: ReactNode;
}

const UserSessionProvider = ({ children }: UserSessionProviderProps) => {
    // get the initial token from the session
    // forcefully run validation when retrieving it
    const [currentToken, setCurrentToken] = useState(() => getToken(true));

    const sessionId = useMemo(() => {
        if (currentToken) {
            return (extractFromJWT(currentToken) as { sessionId: { $oid: string } }).sessionId.$oid as string;
        }

        return null;
    }, [currentToken]);

    const selectedCompanyId = useMemo(() => {
        if (currentToken) {
            try {
                const payload = extractFromJWT<{ selectedCompanyId?: { $oid: string } }>(currentToken);
                return payload.selectedCompanyId?.$oid || null;
            } catch {
                return null;
            }
        }
        return null;
    }, [currentToken]);

    // listen for token updates and update the state
    useEffect(() => onTokenUpdate(setCurrentToken), [setCurrentToken]);

    // session lifecycle: activity tracking, inactivity warning, conditional refresh, logout
    const { logout } = useSessionLifecycle(currentToken);

    // query to fetch user document
    const { data, refetch, loading } = useQuery(FetchCurrentUser, {
        // use network only policy to avoid returning stale data
        fetchPolicy: 'cache-and-network',
    });

    // Derive companyId for both tenant users and super admins
    const user = currentToken ? data?.currentUser?.user || null : null;
    const companyId = useMemo(() => {
        if (user?.__typename === 'TenantUser' && user.company) {
            return user.company.id;
        }
        return selectedCompanyId;
    }, [user, selectedCompanyId]);

    // Fetch full company object once, shared via context
    const { data: companyData, loading: companyLoading } = useQuery(FetchCompanyDetails, {
        variables: { id: companyId! },
        skip: !companyId,
        fetchPolicy: 'cache-and-network',
    });

    const company = useMemo(() => {
        const c = companyData?.company ?? null;
        // Avoid stale data from a previous company while the new query is loading
        if (c && c.id !== companyId) {
            return null;
        }
        return c;
    }, [companyData, companyId]);

    // we want to refetch the user document whenever the token changed
    useEffect(() => {
        refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentToken]);

    // apply the user's saved language preference once it loads (server is the cross-device source of truth)
    useEffect(() => {
        const preferred = user?.language;
        if (preferred === undefined) {
            return;
        }
        const next = preferred ?? 'en';
        if (next !== i18n.resolvedLanguage) {
            void i18n.changeLanguage(next);
        }
    }, [user?.language]);

    const context = useMemo(
        (): ContextValue => ({
            token: currentToken,
            // if there's no token then we right away nullify the user document
            user: currentToken ? data?.currentUser?.user || null : null,
            activeRole: currentToken ? data?.currentUser?.activeRole || null : null,
            sessionId,
            selectedCompanyId,
            company,
            logout,
        }),
        [currentToken, data, sessionId, selectedCompanyId, company, logout]
    );

    if (context.token && !context?.user && loading) {
        return <Loader />;
    }

    if (context.token && context.user && companyId && !company && companyLoading) {
        return <Loader />;
    }

    return <Context.Provider value={context}>{children}</Context.Provider>;
};

export default UserSessionProvider;
